// RITA-1525 — Lesen und Schreiben des Vertragsdokuments als JSON (Format v2).
//
// Das Dokument liegt im Feld `document`, nicht als JSON-Text in `content`. Unbekannte Formate und
// Versionen werden abgelehnt. Nur der Leser gespeicherter Vorlagen erhält unbekannte, formgültige
// Platzhalter als Text; der strenge Leser bleibt für Modellprüfungen verfügbar.
import { mitBlockKennungen, platzhalter, text } from './erzeugen';
import { istVertragPlatzhalter, VertragPlatzhalterKey } from './platzhalterkatalog';
import {
  Block,
  Fusszeile,
  HorizontalePosition,
  Inlineknoten,
  Kopfzeile,
  Schrifteinstellungen,
  Seiteneinstellungen,
  Vertragsdokument,
  VERTRAGSDOKUMENT_FORMAT,
  VERTRAGSDOKUMENT_VERSION,
  Zeichenformat,
  Zeilenblock,
} from './types';

export type DokumentfehlerCode = 'FORMAT' | 'VERSION' | 'STRUKTUR';

export class Dokumentfehler extends Error {
  code: DokumentfehlerCode;

  constructor(code: DokumentfehlerCode, message: string) {
    super(message);
    this.name = 'Dokumentfehler';
    this.code = code;
  }
}

const POSITIONEN: readonly HorizontalePosition[] = ['links', 'mitte', 'rechts'];

const istObjekt = (wert: unknown): wert is Record<string, unknown> => typeof wert === 'object' && wert !== null;
const istZahl = (wert: unknown): wert is number => typeof wert === 'number' && Number.isFinite(wert);
const istText = (wert: unknown): wert is string => typeof wert === 'string';

function struktur(bedingung: boolean, stelle: string): void {
  if (!bedingung) throw new Dokumentfehler('STRUKTUR', `Ungültiges Vertragsdokument: ${stelle}`);
}

const PLATZHALTER_SCHLUESSEL = /^[A-Z][A-Z0-9_]{0,63}$/;

/**
 * Format von Textlauf und Platzhalter: nur `true` zählt (gleiche Regel wie im Server-Prüfmodul). Die eigene
 * Schriftgröße (RITA-1534) zählt nur als positive Zahl; alles andere heißt „keine eigene Größe“ – der Server
 * lehnt solche Werte ohnehin ab.
 */
function leseFormat(knoten: Record<string, unknown>): Zeichenformat {
  const format: Zeichenformat = {
    fett: knoten.fett === true,
    kursiv: knoten.kursiv === true,
    unterstrichen: knoten.unterstrichen === true,
  };
  if (istZahl(knoten.groessePt) && knoten.groessePt > 0) format.groessePt = knoten.groessePt;
  return format;
}

function leseInline(wert: unknown, stelle: string, unbekanntePlatzhalter?: Set<string>): Inlineknoten {
  struktur(istObjekt(wert), `${stelle} ist kein Knoten`);
  const knoten = wert as Record<string, unknown>;
  switch (knoten.typ) {
    case 'text': {
      struktur(istText(knoten.text), `${stelle}: Text fehlt`);
      return text(knoten.text as string, leseFormat(knoten));
    }
    case 'platzhalter': {
      const { schluessel } = knoten;
      if (
        unbekanntePlatzhalter &&
        istText(schluessel) &&
        PLATZHALTER_SCHLUESSEL.test(schluessel) &&
        !istVertragPlatzhalter(schluessel)
      ) {
        unbekanntePlatzhalter.add(schluessel);
        // Ein unbekannter Platzhalter wird Text; sein Format bleibt dabei erhalten (RITA-1533).
        return text(`{{${schluessel}}}`, leseFormat(knoten));
      }
      struktur(
        istText(schluessel) && istVertragPlatzhalter(schluessel),
        `${stelle}: unbekannter Platzhalter ${String(schluessel)}`,
      );
      return platzhalter(schluessel as VertragPlatzhalterKey, leseFormat(knoten));
    }
    case 'kaestchen':
      return { typ: 'kaestchen', angekreuzt: knoten.angekreuzt === true };
    case 'zeilenumbruch':
      return { typ: 'zeilenumbruch' };
    default:
      throw new Dokumentfehler('STRUKTUR', `Ungültiges Vertragsdokument: ${stelle}: unbekannter Knotentyp`);
  }
}

function leseInlines(wert: unknown, stelle: string, unbekanntePlatzhalter?: Set<string>): Inlineknoten[] {
  struktur(Array.isArray(wert), `${stelle} ist keine Liste`);
  return (wert as unknown[]).map((knoten, index) => leseInline(knoten, `${stelle}[${index}]`, unbekanntePlatzhalter));
}

function leseBlock(wert: unknown, stelle: string, umbruchErlaubt: boolean, unbekanntePlatzhalter?: Set<string>): Block {
  struktur(istObjekt(wert), `${stelle} ist kein Block`);
  const block = wert as Record<string, unknown>;
  const id = istText(block.id) ? block.id : '';
  switch (block.typ) {
    case 'absatz':
      return { typ: 'absatz', id, inhalt: leseInlines(block.inhalt, `${stelle}.inhalt`, unbekanntePlatzhalter) };
    case 'liste': {
      struktur(Array.isArray(block.eintraege), `${stelle}.eintraege ist keine Liste`);
      const eintraege = (block.eintraege as unknown[]).map((eintrag, index) =>
        leseInlines(eintrag, `${stelle}.eintraege[${index}]`, unbekanntePlatzhalter),
      );
      return { typ: 'liste', id, eintraege };
    }
    case 'seitenumbruch':
      struktur(umbruchErlaubt, `${stelle}: Seitenumbruch nur im Text erlaubt`);
      return { typ: 'seitenumbruch', id };
    default:
      throw new Dokumentfehler('STRUKTUR', `Ungültiges Vertragsdokument: ${stelle}: unbekannter Blocktyp`);
  }
}

const KENNUNG = /^[A-Za-z0-9_-]*$/;

function leseBloecke(
  wert: unknown,
  stelle: string,
  umbruchErlaubt: boolean,
  unbekanntePlatzhalter?: Set<string>,
): Block[] {
  struktur(Array.isArray(wert), `${stelle} ist keine Liste`);
  const bloecke = (wert as unknown[]).map((block, index) =>
    leseBlock(block, `${stelle}[${index}]`, umbruchErlaubt, unbekanntePlatzhalter),
  );
  const gesehen = new Set<string>();
  bloecke.forEach((block, index) => {
    struktur(KENNUNG.test(block.id), `${stelle}[${index}].id enthält unzulässige Zeichen`);
    struktur(block.id === '' || !gesehen.has(block.id), `${stelle}[${index}].id ist doppelt`);
    gesehen.add(block.id);
  });
  return bloecke;
}

function leseSeite(wert: unknown): Seiteneinstellungen {
  struktur(istObjekt(wert), 'seite fehlt');
  const seite = wert as Record<string, unknown>;
  struktur(seite.format === 'A4', 'seite.format muss A4 sein');
  struktur(istObjekt(seite.raender), 'seite.raender fehlt');
  const raender = seite.raender as Record<string, unknown>;
  const rand = (name: string): number => {
    const zahl = raender[name];
    struktur(istZahl(zahl) && zahl >= 0, `seite.raender.${name} ist keine Zahl`);
    return zahl as number;
  };
  struktur(
    istZahl(seite.kopfHoeheMm) && istZahl(seite.fussHoeheMm) && seite.kopfHoeheMm >= 0 && seite.fussHoeheMm >= 0,
    'Kopf-/Fußhöhe fehlt oder ist negativ',
  );
  return {
    format: 'A4',
    raender: { obenMm: rand('obenMm'), rechtsMm: rand('rechtsMm'), untenMm: rand('untenMm'), linksMm: rand('linksMm') },
    kopfHoeheMm: seite.kopfHoeheMm as number,
    fussHoeheMm: seite.fussHoeheMm as number,
  };
}

function leseSchrift(wert: unknown): Schrifteinstellungen {
  struktur(istObjekt(wert), 'schrift fehlt');
  const schrift = wert as Record<string, unknown>;
  struktur(schrift.familie === 'Liberation Sans', 'schrift.familie wird nicht unterstützt');
  struktur(istZahl(schrift.groessePt) && (schrift.groessePt as number) > 0, 'schrift.groessePt fehlt');
  struktur(istZahl(schrift.zeilenhoehe) && (schrift.zeilenhoehe as number) > 0, 'schrift.zeilenhoehe fehlt');
  const ergebnis: Schrifteinstellungen = {
    familie: 'Liberation Sans',
    groessePt: schrift.groessePt as number,
    zeilenhoehe: schrift.zeilenhoehe as number,
  };
  // Abstand nach Absatz (RITA-1571): fehlt er, bleibt er weg (= bisherige 0.6 em), damit eine alte Vorlage beim
  // Speichern unverändert bleibt. Nur eine Zahl ab 0 zählt; der Server lehnt andere Werte ohnehin ab.
  if (istZahl(schrift.absatzabstandEm) && schrift.absatzabstandEm >= 0) {
    ergebnis.absatzabstandEm = schrift.absatzabstandEm;
  }
  return ergebnis;
}

function lesePosition(wert: unknown, stelle: string): HorizontalePosition {
  struktur(istText(wert) && (POSITIONEN as readonly string[]).includes(wert), `${stelle}: unbekannte Position`);
  return wert as HorizontalePosition;
}

function leseKopf(wert: unknown, unbekanntePlatzhalter?: Set<string>): Kopfzeile {
  struktur(istObjekt(wert), 'kopf fehlt');
  const kopf = wert as Record<string, unknown>;
  struktur(istObjekt(kopf.logo), 'kopf.logo fehlt');
  const logo = kopf.logo as Record<string, unknown>;
  // Nur die id der Vertragsart wird gespeichert; ob es sie noch gibt, prüft die Oberfläche beim Anzeigen.
  struktur(
    logo.vertragsartId === null ||
      (istZahl(logo.vertragsartId) && Number.isInteger(logo.vertragsartId) && logo.vertragsartId > 0),
    'kopf.logo.vertragsartId ist ungültig',
  );
  struktur(istZahl(logo.hoeheMm) && (logo.hoeheMm as number) > 0, 'kopf.logo.hoeheMm fehlt');
  return {
    logo: {
      vertragsartId: logo.vertragsartId as number | null,
      position: lesePosition(logo.position, 'kopf.logo.position'),
      hoeheMm: logo.hoeheMm as number,
    },
    inhalt: mitBlockKennungen(
      leseBloecke(kopf.inhalt, 'kopf.inhalt', false, unbekanntePlatzhalter) as Zeilenblock[],
      'k',
    ),
  };
}

function leseFuss(wert: unknown, unbekanntePlatzhalter?: Set<string>): Fusszeile {
  struktur(istObjekt(wert), 'fuss fehlt');
  const fuss = wert as Record<string, unknown>;
  struktur(istObjekt(fuss.seitenzahl), 'fuss.seitenzahl fehlt');
  const seitenzahl = fuss.seitenzahl as Record<string, unknown>;
  return {
    inhalt: mitBlockKennungen(
      leseBloecke(fuss.inhalt, 'fuss.inhalt', false, unbekanntePlatzhalter) as Zeilenblock[],
      'f',
    ),
    seitenzahl: {
      anzeigen: seitenzahl.anzeigen === true,
      position: lesePosition(seitenzahl.position, 'fuss.seitenzahl.position'),
    },
  };
}

/** Erkennt die Formatkennung in JSON-Text; prüft nicht dessen Aufbau. Vorlagen liefern `document` als Objekt. */
export function istVertragsdokumentJson(gespeichert: string | null | undefined): boolean {
  if (!gespeichert) return false;
  const anfang = gespeichert.trimStart();
  return anfang.startsWith('{') && anfang.includes(`"${VERTRAGSDOKUMENT_FORMAT}"`);
}

function leseDokument(quelle: unknown, unbekanntePlatzhalter?: Set<string>): Vertragsdokument {
  let roh: unknown = quelle;
  if (typeof quelle === 'string') {
    try {
      roh = JSON.parse(quelle);
    } catch {
      throw new Dokumentfehler('FORMAT', 'Der gespeicherte Inhalt ist kein gültiges JSON.');
    }
  }
  if (!istObjekt(roh) || roh.format !== VERTRAGSDOKUMENT_FORMAT) {
    throw new Dokumentfehler('FORMAT', 'Der gespeicherte Inhalt ist kein Vertragsdokument.');
  }
  if (roh.version !== VERTRAGSDOKUMENT_VERSION) {
    throw new Dokumentfehler(
      'VERSION',
      `Dokumentversion ${String(roh.version)} wird nicht unterstützt (erwartet ${VERTRAGSDOKUMENT_VERSION}).`,
    );
  }
  return {
    format: VERTRAGSDOKUMENT_FORMAT,
    version: VERTRAGSDOKUMENT_VERSION,
    seite: leseSeite(roh.seite),
    schrift: leseSchrift(roh.schrift),
    kopf: leseKopf(roh.kopf, unbekanntePlatzhalter),
    text: mitBlockKennungen(leseBloecke(roh.text, 'text', true, unbekanntePlatzhalter)),
    fuss: leseFuss(roh.fuss, unbekanntePlatzhalter),
  };
}

/** Liest JSON-Text oder einen bereits geparsten Wert; unbekannte Platzhalter sind Fehler. */
export function leseVertragsdokument(quelle: unknown): Vertragsdokument {
  return leseDokument(quelle);
}

/** Öffnet gespeicherte Vorlagen auch dann, wenn ein formgültiger Platzhalter nicht mehr im Katalog steht. */
export function leseGespeichertesVertragsdokument(quelle: unknown): {
  dokument: Vertragsdokument;
  unbekanntePlatzhalter: string[];
} {
  const unbekanntePlatzhalter = new Set<string>();
  const dokument = leseDokument(quelle, unbekanntePlatzhalter);
  return { dokument, unbekanntePlatzhalter: Array.from(unbekanntePlatzhalter) };
}

export function schreibeVertragsdokument(dokument: Vertragsdokument): string {
  return JSON.stringify(dokument);
}
